import { 
    InteractionType, 
    InteractionStatus, 
    InteractionRequiredAuthError,
    BrowserAuthError 
} from "@azure/msal-browser";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useMsalAuthentication } from "@azure/msal-react";
import { Flex } from "antd";
import { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router-dom";

import styles from "./MSPlannerPage.module.scss";

import { MicrosoftIcon } from "../../assets/icons/microsoftIcon";
import { StepsAlt } from "../../components/Steps";

export const MSPlannerPage = () => {
    const params = useParams();
    const [popupBlocked, setPopupBlocked] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    
    const request = { scopes: ["https://globaldisco.crm.dynamics.com/.default"], prompt: "consent" };
    const { error, login } = useMsalAuthentication(InteractionType.Silent, request);
    const { instance, accounts, inProgress } = useMsal();

    useEffect(() => {
        if (error instanceof InteractionRequiredAuthError) {
            handlePopupLogin();
        }
    }, [error]);

    useEffect(() => {
        instance.setActiveAccount(accounts[0] || null);
    }, [accounts]);

    const handlePopupLogin = async () => {
        try {
            setPopupBlocked(false);
            setLoginError(null);
            await login(InteractionType.Popup, request);
        } catch (error: any) {
            console.error('Login error:', error);
            
            // Check for popup blocked error
            if (error instanceof BrowserAuthError) {
                if (error.errorCode === 'popup_window_error' || 
                    error.errorCode === 'empty_window_error' ||
                    error.message?.includes('popup') ||
                    error.message?.includes('blocked')) {
                    setPopupBlocked(true);
                    setLoginError('Popup was blocked by your browser. Please allow popups and try again.');
                } else {
                    setLoginError(`Authentication failed: ${error.message}`);
                }
            } else {
                setLoginError('An unexpected error occurred during login.');
            }
        }
    };

    const handleRedirectLogin = async () => {
        try {
            setLoginError(null);
            await instance.loginRedirect(request);
        } catch (error: any) {
            setLoginError(`Redirect login failed: ${error.message}`);
        }
    };

    if (inProgress === InteractionStatus.Startup) {
        return <div>Initializing...</div>;
    }

    return (
        <Flex
            vertical
            align="center"
            gap={20}
            justify="flex-start"
            className={styles.root}
        >
            <AuthenticatedTemplate>
                <StepsAlt
                    totalSteps={5}
                    step={Object.keys(params).length + 1}
                    stepsDescription={{
                        1: "Select the environment (instance) you want to import data from",
                        2: "Review and start the import",
                        3: "Wait while we prepare the import",
                        4: "Results"
                    }}
                >
                    <Flex
                        vertical
                        align="center"
                        justify="center"
                        className={styles.contentFrame}
                    >
                        <Outlet />
                    </Flex>
                </StepsAlt>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <StepsAlt
                    totalSteps={5}
                    step={0}
                    stepsDescription={{ 0: "Connect to your Microsoft Planner account" }}
                >
                <Flex
                    vertical
                    align="center"
                    gap={20}
                    justify="center"
                    className={styles.root}
                >
                    {loginError && (
                        <div style={{ 
                            color: 'red', 
                            textAlign: 'center', 
                            marginBottom: 16,
                            padding: 12,
                            background: '#ffebee',
                            borderRadius: 4,
                            border: '1px solid #ffcdd2'
                        }}>
                            {loginError}
                        </div>
                    )}
                    
                    {popupBlocked ? (
                        <Flex vertical gap={12} align="center">
                            <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                <strong>Popup blocked detected</strong>
                                <p>Please allow popups for this site or use redirect login</p>
                            </div>
                            <button
                                onClick={handlePopupLogin}
                                className={styles.msLoginButton}
                                disabled={inProgress === InteractionStatus.Login}
                            >
                                <MicrosoftIcon className={styles.msIcon} />
                                Try Popup Again
                            </button>
                            <button
                                onClick={handleRedirectLogin}
                                className={styles.msLoginButton}
                                disabled={inProgress === InteractionStatus.Login}
                            >
                                <MicrosoftIcon className={styles.msIcon} />
                                Login with Redirect
                            </button>
                        </Flex>
                    ) : (
                        <button
                            onClick={handlePopupLogin}
                            className={styles.msLoginButton}
                            disabled={inProgress === InteractionStatus.Login}
                        >
                            <MicrosoftIcon className={styles.msIcon} />
                            {inProgress === InteractionStatus.Login ? "Restoring session..." : "Login with Microsoft"}
                        </button>
                    )}
                </Flex>
                </StepsAlt>
            </UnauthenticatedTemplate>
        </Flex>
    );
};